/** Enterprise Home building blocks. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Sun, ExternalLink, CheckCircle2, RefreshCw, Inbox } from "lucide-react";
import { KpiCard, StatusPill, OsEmpty, SkeletonRows, fmtValue } from "@/components/os/OsPrimitives";

export const greeting = (d = new Date()) => {
  const h = d.getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};
const firstName = (u) => (u?.name || u?.email || "there").split(/[\s@]/)[0];

export function HomeHeader({ user, total, onStart, refreshedAt, onRefresh }) {
  return (
    <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4" data-testid="home-header">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--st-text-subtle)] font-semibold">
          {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })} · {user?.role_label}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[color:var(--st-text)] mt-1" data-testid="home-greeting">
          {greeting()}, {firstName(user)}.
        </h1>
        <p className="text-sm text-[color:var(--st-text-muted)] mt-1.5">
          {total > 0 ? <>Here's what needs your attention today — <span className="font-semibold text-[color:var(--st-text)]">{total} item{total === 1 ? "" : "s"}</span>.</>
            : "Here's what needs your attention today."}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={onRefresh} data-testid="home-refresh" title={refreshedAt ? `Refreshed ${refreshedAt.toLocaleTimeString()}` : "Refresh"}
          className="h-10 w-10 grid place-items-center rounded-full border border-[color:var(--st-border)] bg-white text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] hover:border-[color:var(--st-border-strong)] transition-colors">
          <RefreshCw className="h-4 w-4" strokeWidth={1.8} />
        </button>
        <button onClick={onStart} data-testid="home-start-my-day"
          className="h-10 px-4 rounded-full bg-[color:var(--st-accent)] text-white text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-110 active:scale-[0.98] transition-[filter,transform] shadow-[0_6px_16px_rgba(36,107,253,0.28)]">
          <Sun className="h-4 w-4" strokeWidth={2} /> Start my day
        </button>
      </div>
    </header>
  );
}

export function KpiStrip({ kpis, loading, compact = false }) {
  const navigate = useNavigate();
  const grid = compact ? "grid grid-cols-3 gap-3" : "grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3";
  if (loading) return <div className={grid}>{Array.from({ length: compact ? 3 : 6 }).map((_, i) => <div key={i} className="os-skeleton h-[92px]" />)}</div>;
  return (
    <div className={grid} data-testid="home-kpis">
      {kpis.map((k, i) => (
        <KpiCard key={k.key} index={i} label={k.label} value={k.value} fmt={k.fmt} hint={k.hint} tone={k.tone}
          onClick={() => navigate(k.route)} testId={`home-kpi-${k.key}`} />
      ))}
    </div>
  );
}

const AI_HINTS = ["Show quotations pending for more than 7 days", "Create a follow-up for STX-Q-2026-00218",
  "Which deliveries are delayed this week?", "Show open orders for ABC Technologies"];

export function AiBar() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const go = (text) => { const t = (text || q).trim(); if (t.length < 3) return; navigate(`/copilot?q=${encodeURIComponent(t)}`); };
  return (
    <section className="os-card p-4 sm:p-5 relative overflow-hidden" data-testid="home-ai">
      <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[color:var(--st-ai)]/[0.07] blur-2xl pointer-events-none" />
      <div className="flex items-center gap-2 mb-3">
        <span className="h-7 w-7 rounded-lg grid place-items-center text-white os-ai-btn"><Sparkles className="h-4 w-4" strokeWidth={2} /></span>
        <span className="font-display font-bold text-sm tracking-tight text-[color:var(--st-text)]">SUNTRONIX AI</span>
        <StatusPill tone="ai" dot={false}>Natural language</StatusPill>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); go(); }} className="flex items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="home-ai-input"
          placeholder="Ask anything or tell Suntronix what you want to do…"
          className="flex-1 h-11 px-4 rounded-full border border-[color:var(--st-border)] bg-[color:var(--st-bg)] text-sm outline-none focus:border-[color:var(--st-ai)] focus:ring-4 focus:ring-[color:var(--st-ai)]/15 transition-[border-color,box-shadow]" />
        <button type="submit" data-testid="home-ai-submit" aria-label="Ask Suntronix AI"
          className="h-11 w-11 rounded-full grid place-items-center text-white os-ai-btn shrink-0"><ArrowRight className="h-4 w-4" strokeWidth={2.2} /></button>
      </form>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {AI_HINTS.map((h) => (
          <button key={h} onClick={() => go(h)} data-testid="home-ai-hint"
            className="text-[11px] px-2.5 py-1 rounded-full border border-[color:var(--st-border)] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-ai)] hover:border-[color:var(--st-ai)]/40 hover:bg-[color:var(--st-ai-soft)] transition-colors">{h}</button>
        ))}
      </div>
    </section>
  );
}

export const AC_TABS = [
  { key: "today", label: "Today" }, { key: "overdue", label: "Overdue", tone: "critical" },
  { key: "waiting_customer", label: "Waiting Customer", tone: "info" }, { key: "waiting_vendor", label: "Waiting Vendor", tone: "warn" },
  { key: "critical", label: "Critical", tone: "critical" }, { key: "completed", label: "Completed", tone: "good" },
];
const PRIO_TONE = { critical: "critical", high: "warn", medium: "neutral", low: "neutral" };
const WAIT_LABEL = { CUSTOMER: "Customer", VENDOR: "Vendor", INTERNAL: "Internal" };

export function ActionCentrePanel({ ac, loading, onAction, onComplete }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("today");
  const items = ac?.[tab] || [];
  const counts = ac?.counts || {};
  return (
    <section className="os-card overflow-hidden" data-testid="home-action-centre">
      <div className="px-4 sm:px-5 pt-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-subtle)] font-semibold">02 · Work queue</div>
          <h2 className="font-display font-bold text-base md:text-lg text-[color:var(--st-text)] tracking-tight">My Action Centre</h2>
        </div>
        <button onClick={() => navigate("/today")} data-testid="home-ac-open-full"
          className="text-[12px] font-medium text-[color:var(--st-accent)] hover:underline inline-flex items-center gap-1">Open full Action Centre <ArrowRight className="h-3.5 w-3.5" /></button>
      </div>
      <div className="px-3 sm:px-4 mt-3 flex gap-1 overflow-x-auto border-b border-[color:var(--st-border)]" role="tablist">
        {AC_TABS.map((t) => {
          const active = tab === t.key; const n = counts[t.key] ?? 0;
          return (
            <button key={t.key} role="tab" aria-selected={active} onClick={() => setTab(t.key)} data-testid={`home-ac-tab-${t.key}`}
              className={"relative h-10 px-3 text-[12.5px] font-medium whitespace-nowrap inline-flex items-center gap-1.5 transition-colors " + (active ? "text-[color:var(--st-text)]" : "text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]")}>
              {t.label}
              <span className={"min-w-[20px] h-5 px-1.5 rounded-full text-[10.5px] font-semibold grid place-items-center " + (active ? "bg-[color:var(--st-text)] text-white" : "bg-[color:var(--st-surface-alt)] text-[color:var(--st-text-muted)]")}>{n}</span>
              {active && <span className="absolute left-2 right-2 -bottom-px h-[2px] rounded-full bg-[color:var(--st-accent)]" />}
            </button>
          );
        })}
      </div>
      <div className="min-h-[180px]">
        {loading ? <div className="p-4"><SkeletonRows rows={5} /></div>
          : items.length === 0 ? (
            <OsEmpty testId={`home-ac-empty-${tab}`} icon={tab === "completed" ? Inbox : CheckCircle2} tone={tab === "completed" ? "neutral" : "good"}
              title={tab === "completed" ? "Nothing completed yet today" : "You're all caught up."}
              description={tab === "completed" ? "Items you finish will show here." : `No ${AC_TABS.find((t) => t.key === tab)?.label.toLowerCase()} items require attention.`} />
          ) : (
            <ul className="divide-y divide-[color:var(--st-border)]" data-testid={`home-ac-list-${tab}`}>
              {items.slice(0, 8).map((it) => (
                <li key={it.key} className="px-4 sm:px-5 py-3 flex items-start gap-3 hover:bg-[color:var(--st-surface-alt)]/60 transition-colors group" data-testid={`home-ac-item-${it.key}`}>
                  <span className={"mt-1.5 h-2 w-2 rounded-full shrink-0 " + (it.priority === "critical" ? "bg-[color:var(--st-critical)]" : it.is_overdue ? "bg-[color:var(--st-warn)]" : "bg-[color:var(--st-accent)]")} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-[color:var(--st-text)] truncate">{it.title}</div>
                    <div className="text-[11.5px] text-[color:var(--st-text-muted)] truncate mt-0.5">
                      {it.customer && <span className="font-medium text-[color:var(--st-text)]/80">{it.customer} · </span>}
                      {it.record && <span className="font-mono">{it.record.length > 24 ? it.record.slice(0, 8) : it.record} · </span>}
                      {it.next_action || it.category}
                      {it.value_inr != null && <span className="ml-1 font-mono">· {fmtValue(it.value_inr, "inr")}</span>}
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5 shrink-0">
                    {it.is_overdue && <StatusPill tone="critical">{it.overdue_days}d overdue</StatusPill>}
                    {!it.is_overdue && it.priority && it.priority !== "medium" && <StatusPill tone={PRIO_TONE[it.priority]}>{it.priority}</StatusPill>}
                    <StatusPill tone="neutral" dot={false}>{WAIT_LABEL[it.waiting_on] || "Internal"}</StatusPill>
                  </div>
                  {tab !== "completed" && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => onAction(it)} data-testid={`home-ac-open-${it.key}`}
                        className="h-7 px-2.5 rounded-md text-[11px] font-medium bg-[color:var(--st-text)] text-white hover:bg-[#17263C] inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" />Open</button>
                      {it.can_complete !== false && (
                        <button onClick={() => onComplete(it)} data-testid={`home-ac-done-${it.key}`} title="Mark complete"
                          className="h-7 w-7 rounded-md border border-[color:var(--st-border)] text-[color:var(--st-success)] hover:bg-[color:var(--st-success-soft)] grid place-items-center"><CheckCircle2 className="h-3.5 w-3.5" /></button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        {items.length > 8 && (
          <div className="px-4 py-2.5 border-t border-[color:var(--st-border)] text-center">
            <button onClick={() => navigate("/today")} className="text-[12px] font-medium text-[color:var(--st-accent)] hover:underline" data-testid="home-ac-more">+ {items.length - 8} more in Action Centre</button>
          </div>
        )}
      </div>
    </section>
  );
}

const TONE_TXT = { good: "text-[color:var(--st-success)]", warn: "text-[color:var(--st-warn)]", bad: "text-[color:var(--st-critical)]" };
export function PerformancePanel({ groups, loading }) {
  const navigate = useNavigate();
  if (loading) return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="os-skeleton h-24" />)}</div>;
  return (
    <div className="space-y-5" data-testid="home-performance">
      {groups.map((g) => (
        <div key={g.label} data-testid={`home-perf-${g.label.toLowerCase()}`}>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-subtle)] font-semibold mb-2">{g.label}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {g.tiles.map((t, i) => (
              <button key={t.key} onClick={() => navigate(t.route)} data-testid={`home-perf-tile-${t.key}`}
                className="os-card os-rise text-left px-4 py-3.5" style={{ animationDelay: `${i * 40}ms` }}>
                <div className="text-[10.5px] uppercase tracking-[0.12em] text-[color:var(--st-text-muted)] font-medium">{t.label}</div>
                <div className={"font-display text-2xl font-bold tracking-tight mt-1 tabular-nums " + (TONE_TXT[t.tone] || "text-[color:var(--st-text)]")}>{fmtValue(t.value, t.fmt)}</div>
                {t.hint && <div className="text-[11px] text-[color:var(--st-text-subtle)] mt-0.5 truncate">{t.hint}</div>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function SectionLabel({ step, title, hint, action }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-subtle)] font-semibold">{step}</div>
        <h2 className="font-display font-bold text-base md:text-lg text-[color:var(--st-text)] tracking-tight">{title}</h2>
      </div>
      {hint && <span className="text-[11px] text-[color:var(--st-text-subtle)]">{hint}</span>}
      {action}
    </div>
  );
}
